import React from "react";
import {Box, Collapse, Divider, Link, Typography} from "@mui/material";
import {ExpandLess, ExpandMore} from "@mui/icons-material";
import {
    elementIncludesImage, enrichWithUuid,
    getAttribute,
    getElementTextAlign,
    isCtaButtonClass,
    isUrlAbsolute,
    ListType,
    RichTextElement,
    RichTextProps
} from "./CCORichTextHelper";
import C041Video from "../../content/C041Video/C041Video";
import {Cmhtml, CmVideo} from "../../../../packages/graphql-layer/src";
import PlacementItem from "../../layouts/body/PlacementItem";
import {generateDownloadUrl, getIdFromDataHref, getIdFromHref, resolveLinkWithPlaceholder} from "../../../utility/navigationUtil";
import C067HtmlFragment from "../../content/C067HtmlFragment/C067HtmlFragment";
import {
    blockQuoteStyle,
    imgInnerBoxStyle,
    imgOuterBoxStyle,
    MARGINS,
    olLiStyle,
    olStyle,
    ulLiStyle,
    ulStyle
} from "./CCORichTextStyles";
import CCODownloadButton from "../CCODownloadButton";
import CCOOpenInNewTabIcon from "../CCOOpenInNewTabIcon";
import {Variant} from "@mui/material/styles/createTypography";
import CCOHeadline from "../CCOHeadline";
import {t} from "i18next";
import Button from "@mui/material/Button";
import {BUTTON_DEFAULT_COLOR, buttonClasses} from "../../layouts/navigation/QuickAccessButton";

const getTarget = (element: RichTextElement) => element?.attributes?.find((attribute) => attribute.name === 'data-show')?.value === 'new' ? '_blank' : '_self';
const isDownloadTarget = (link: string) => link?.includes(":CMDownload");
const getDownloadTargetLink = (link: string) => {
    const page = link?.split('#')?.[1];
    return generateDownloadUrl(getIdFromHref(link)) + (page ? '#page=' + page : '');
}

export function renderShowMoreContainerElement(element: RichTextElement,
                                               renderChildrenRecursively: (...args: any[]) => JSX.Element | null,
                                               showMore: boolean | undefined,
                                               setShowMore: (...args: any[]) => void,
                                               key: any): JSX.Element {
    return <React.Fragment key={element.uuid}>
        {(element.children && element.children[0] && element.children[0].children) ?
            <React.Fragment
                key={element.uuid}>{(element.children![0].children ?? []).map(renderChildrenRecursively)}</React.Fragment>
            : null}
        {(element.children && element.children[1] && element.children[1].children) ?
            <Box key={element.uuid} marginTop={8}>
                <Collapse key={element.uuid} in={showMore} timeout="auto" unmountOnExit
                          sx={{marginBottom: 8}}>
                    {(element.children![1].children ?? []).map(renderChildrenRecursively)}
                </Collapse>
                <Divider key={key + "-divider"}></Divider>
                <Box key={key + "-box"} display={'flex'} justifyContent={'center'} marginTop={6}>
                    <Typography key={key + "-text"} display={'flex'}
                                alignItems={'center'}
                                variant={'linkLabel'}
                                color={'primary.main'}
                                sx={{marginX: 1}}
                                onClick={() => setShowMore(!showMore)}>{showMore ? t("coo_show_less") : t("coo_show_more")}
                        {showMore ? <ExpandLess key={key + "-less"}/> :
                            <ExpandMore key={key + "-more"}/>}
                    </Typography>
                </Box>
            </Box> : null}
    </React.Fragment>
}

export function renderMediaElement(element: RichTextElement,
                                   key: any): JSX.Element {
    const srcDataAttribute = getAttribute(element.attributes!, "data-src")

    const isVideo = srcDataAttribute.includes(":CMVideo")
    const isPicture = srcDataAttribute.includes(":CMPicture")

    let videoUri = ""
    if (isVideo) {
        videoUri = srcDataAttribute.split(":CMVideo")[0]
    }
    const className = getAttribute(element.attributes!, "class")

    if (!className) {
        return isVideo ?
            <C041Video key={element.uuid} video={{dataUrl: videoUri} as CmVideo}
                       noContainer={true}></C041Video>
            : isPicture ?
                <Box key={element.uuid} marginTop={4} width={'100%'} sx={imgOuterBoxStyle()}>
                    <Box key={element.uuid + "-box"} width={'100%'} sx={imgInnerBoxStyle()}>
                        <PlacementItem key={element.uuid} item={{type: 'CMPicture'}} CAEGlobalSettings={[]}
                                       csrfToken={''}
                                       richTextItemId={getIdFromHref(srcDataAttribute)}/>
                    </Box>
                </Box>
                // If the element is not an image of video it is a HTMLFragment (work around because MarkUp renders HTML fragment under <img> tag)
                : <C067HtmlFragment key={element.uuid} cmHtml={{html: srcDataAttribute} as Cmhtml}
                                    isPreview={true}></C067HtmlFragment>
    }

    const float: 'left' | 'right' | undefined = className.split('--')[1] as 'left' | 'right' ?? undefined
    return <Box key={key} sx={imgOuterBoxStyle(float)}>
        <Box key={key + "-box"}  sx={imgInnerBoxStyle(float)}>
            {isVideo ?
                <C041Video key={key + "-video"} video={{dataUrl: videoUri} as CmVideo}
                           noContainer={true}></C041Video>
                :
                <PlacementItem key={element.uuid} item={{type: 'CMPicture', clazz: float}}
                               CAEGlobalSettings={[]}
                               csrfToken={''}
                               richTextItemId={getIdFromHref(srcDataAttribute)}/>
            }
        </Box>
    </Box>
}

export function renderLinkElement(element: RichTextElement,
                                  props: RichTextProps,
                                  renderChildrenRecursively: (...args: any[]) => JSX.Element | null,
                                  key: any, parentComponentId: string, pageId: string): JSX.Element {
    if (props.noLinks) {
        return <div key={element.uuid}>{element.children?.map(renderChildrenRecursively)}</div>
    }
    const href = getAttribute(element.attributes!, "data-href") ?? getAttribute(element.attributes!, "href")
    if (href.includes(":CMVideo")) {
        return <C041Video key={element.uuid} video={{dataUrl: href.split(':CMVideo')[0]} as CmVideo}
                          noContainer={true}/>
    }
    const isCMCollection = href.includes(":CMCollection")
    if (isCMCollection) {
        const collectionId = getIdFromHref(href)
        return <PlacementItem key={key} item={{type: 'CMCollection'}} CAEGlobalSettings={[]}
                              csrfToken={''}
                              richTextItemId={collectionId}/>

    }

    if (isDownloadTarget(href)) {
        const hrefLink = getDownloadTargetLink(href);
        return renderDownloadLinkElement(element, hrefLink, key, 'internalDownloadFromRichText', renderChildrenRecursively)
    }

    const isDownload = href.includes("/caas/v1/media")
    if (isDownload && !props.renderDownloadAsLink) {
        const downloadTitle = element?.children && element.children.length > 0 ?
            element!.children![0].data : undefined
        return <CCODownloadButton key={element.uuid} download={[{id: getIdFromDataHref(href)}]} hideSize={true}
                                  showDocumentTitle={true}
                                  downloadTitle={downloadTitle}
                                  target={getTarget(element)}/>
    }

    const hrefLink = isDownload ? generateDownloadUrl(getIdFromDataHref(href)) : resolveLinkWithPlaceholder(href, [localStorage, sessionStorage], parentComponentId, pageId);
    if (hrefLink === null) {
        return <></>;
    }
    return renderDownloadLinkElement(element, hrefLink, key, 'linkInRichText', renderChildrenRecursively);
}

function renderDownloadLinkElement(element: RichTextElement,
                                   href: string, key: any,
                                   dataCypress: string,
                                   renderChildrenRecursively: (...args: any[]) => JSX.Element | null) {
    const target = getTarget(element);
    const openInNewWindow = target === '_blank';
    const isExternal = isUrlAbsolute(href);
    return <Link data-cypress={dataCypress}
                 id={'txt-' + key}
                 key={element.uuid}
                 href={href}
                 underline='hover'
                 variant='linkLabel'
                 target={target}
                 style={{fontSize: 'inherit'}}>
        {(isExternal || openInNewWindow) && <CCOOpenInNewTabIcon/>}
        {element.children?.map((child, index) => renderChildrenRecursively(child, index))}
    </Link>
}

export function renderLiElement(listType: ListType | undefined, element: RichTextElement, renderChildrenRecursively: (...args: any[]) => JSX.Element | null): JSX.Element {
    return listType === 'ul' ? <Box key={element.uuid} component={"li"} textAlign={'start'} sx={ulLiStyle}>
            {element.children?.map(renderChildrenRecursively)}
        </Box>
        : listType === 'ol' ? <Typography key={element.uuid} variant="body1" component="li" sx={olLiStyle}>
                {element.children?.map(renderChildrenRecursively)}
            </Typography>
            : <Box key={element.uuid} component={"li"}
                   textAlign={'start'}>{element.children?.map(renderChildrenRecursively)}</Box>
}

export function renderTextElement(element: RichTextElement,
                                  props: RichTextProps,
                                  renderChildrenRecursively: (...args: any[]) => JSX.Element | null,
                                  key: any,
                                  color: string | undefined,
                                  strong: boolean | undefined): JSX.Element | null {
    const isParagraph = element.name === 'p';
    if (isParagraph) {
        const className = getAttribute(element.attributes!, "class")
        if (className && isCtaButtonClass(className)) {
            return renderCtaButtonElement(element, className)
        }
    }
    const isParagraphWithImage = isParagraph && elementIncludesImage(element);
    const isSingleHeadlineElement = props.headlineElement && props.headlineElement === element.name && element.children?.length === 1

    const marginTop = key === '0' ? 0 : MARGINS[element.name!]?.top;
    const marginBottom = MARGINS[element.name!]?.bottom;
    const variant: any = props.variant ?? (isParagraph ? "body1" : (element.name !== "div" ? (element.name as Variant) : undefined));

    if (isParagraphWithImage) {
        return <React.Fragment key={element.uuid}>
            {element.children?.map(renderChildrenRecursively)}
        </React.Fragment>
    }

    if (isSingleHeadlineElement) {
        return <CCOHeadline headline={element.children?.[0].data} key={element.uuid} margin={{b: 0}}
                            type={"componenthead"}/>
    } else {
        if (!element.uuid) {
            enrichWithUuid(element);
        }
        return (
            <React.Fragment key={element.uuid}>
                <Typography
                    align={getElementTextAlign(props, element)}
                    classes={{root: element.attributes?.map(attribute => attribute.name === "class" ? attribute.value : '').join(' ')}}
                    color={color}
                    component={isParagraph ? 'div' : element.name! as React.ElementType}
                    fontWeight={strong ? 500 : undefined}
                    gutterBottom
                    key={element.uuid}
                    id={props.id}
                    marginBottom={marginBottom}
                    marginTop={marginTop}
                    variant={variant}
                >
                    {element.children?.map(renderChildrenRecursively)}
                </Typography>
            </React.Fragment>)
    }
}

export function renderBlockquoteElement(element: RichTextElement,
                                        props: RichTextProps,
                                        renderElement: (...args: any[]) => JSX.Element | null,
                                        showMore: boolean | undefined,
                                        setShowMore: (...args: any[]) => void,
                                        parentComponentId: string,
                                        pageId: string) {
    return <blockquote key={element.uuid}><Box key={element.uuid}
                                               sx={{display: 'flex', justifyContent: 'center'}}>
        <Typography key={element.uuid} variant={'blockQuote'}
                    color={'primary.main'}
                    sx={blockQuoteStyle}>
            {element.children?.map((e: RichTextElement, index: number) =>
                renderElement(props, e, parentComponentId, pageId, index.toString(), 'blockQuote', showMore, setShowMore, t))}
        </Typography>
    </Box></blockquote>
}

export function renderStrongElement(element: RichTextElement,
                                    renderChildrenRecursively: (...args: any[]) => JSX.Element | null) {
    return <Typography key={element.uuid} style={{fontFamily: 'var(--font-family-bold)', fontWeight: 'bold', fontSize:'inherit'}} component="strong">
        {element.children?.map(renderChildrenRecursively)}
    </Typography>
}

export function renderEmElement(element: RichTextElement,
                                renderChildrenRecursively: (...args: any[]) => JSX.Element | null,
                                color: string | undefined) {
    return <Typography key={element.uuid} component="em" color={color ?? undefined} style={{fontSize: 'inherit'}}>
        {element.children?.map(renderChildrenRecursively)}
    </Typography>
}

export function renderUlElement(element: RichTextElement,
                                props: RichTextProps,
                                renderElement: (...args: any[]) => JSX.Element | null,
                                showMore: boolean | undefined,
                                setShowMore: (...args: any[]) => void,
                                parentComponentId: string,
                                pageId: string) {
    return <ul key={element.uuid} style={ulStyle}>
        {element.children?.map((e, index) =>
            renderElement(props, e, parentComponentId, pageId, index.toString(), props.variant, showMore, setShowMore, 'ul'))}
    </ul>
}

export function renderOlElement(element: RichTextElement,
                                props: RichTextProps,
                                renderElement: (...args: any[]) => JSX.Element | null,
                                showMore: boolean | undefined,
                                setShowMore: (...args: any[]) => void,
                                parentComponentId: string,
                                pageId: string) {
    return <ol key={element.uuid} style={olStyle}>
        {element.children?.map((e, index) =>
            renderElement(props, e, parentComponentId, pageId, index.toString(), props.variant, showMore, setShowMore, 'ol'))}
    </ol>
}

export function renderDefaultElement(element: RichTextElement,
                                     renderChildrenRecursively: (...args: any[]) => JSX.Element | null) {
    return <React.Fragment key={element.uuid}>
        {element.children?.map((child, index) => renderChildrenRecursively(child, index))}
    </React.Fragment>
}

export function renderDivElement(element: RichTextElement,
                                 renderChildrenRecursively: (...args: any[]) => JSX.Element | null) {
    return <Box key={element.uuid}>
        {element.children?.map((child, index) => renderChildrenRecursively(child, index))}
    </Box>
}

function renderCtaButtonElement(element: RichTextElement, className: string) {
    const linkRichTextElement = element.children?.find((child: RichTextElement) => child.name === 'a')
    if (!linkRichTextElement) {
        return null // do not render cta if there is no target link
    }
    const internalUrl = linkRichTextElement?.attributes?.find((attribute) => attribute.name === 'data-href')?.value
    const externalUrl = linkRichTextElement?.attributes?.find((attribute) => attribute.name === 'href')?.value
    const url = internalUrl || externalUrl;
    const href = isDownloadTarget(url!) ? getDownloadTargetLink(url!) : url;
    const target = getTarget(linkRichTextElement);

    const ctaText = linkRichTextElement?.children?.find((child: RichTextElement) => child._type === 'Characters')?.data

    const ctaButtonColorMapping: { [key: string]: string } = {
        'cta-blue': 'bgBlue',
        'cta-yellow': 'bgYellow',
        'cta-green': 'bgGreen'
    };

    const buttonCustomStyle = buttonClasses[ctaButtonColorMapping[className]] || buttonClasses[BUTTON_DEFAULT_COLOR];
    return <Box key={element.uuid} display={'flex'} width={'100%'} justifyContent={'center'} padding={2}>
        <Link id={"cta-" + linkRichTextElement.uuid} key={element.uuid} href={href} target={target}>
            <Button variant="contained" sx={buttonCustomStyle}>
                <Typography variant='ctaButton'>
                    {ctaText}
                </Typography>
            </Button>
        </Link>
    </Box>
}